import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { useAuth } from '../hooks/useAuth';

// Button component
const Button = ({ children, size, variant, className, onClick, ...props }) => (
  <button 
    className={`px-4 py-2 rounded-lg font-medium transition-all ${className}`} 
    onClick={onClick}
    {...props}
  >
    {children}
  </button>
);

// Card component
const Card = ({ children, className }) => <div className={`rounded-xl shadow-lg ${className}`}>{children}</div>;

const formatPrice = (price) => `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatPercentage = (percent) => `${percent.toFixed(2)}%`;

// Mock market data - keeping your existing data structure
const mockMarketData = {
  'BTCUSDT': { symbol: 'BTCUSDT', name: 'Bitcoin', price: 67890.45, change24h: 2.34, volume24h: 28450000000 },
  'ETHUSDT': { symbol: 'ETHUSDT', name: 'Ethereum', price: 3456.78, change24h: -1.23, volume24h: 15200000000 },
  'ADAUSDT': { symbol: 'ADAUSDT', name: 'Cardano', price: 0.4567, change24h: 5.67, volume24h: 890000000 },
  'DOTUSDT': { symbol: 'DOTUSDT', name: 'Polkadot', price: 8.23, change24h: -0.89, volume24h: 450000000 },
  'LINKUSDT': { symbol: 'LINKUSDT', name: 'Chainlink', price: 14.56, change24h: 3.45, volume24h: 780000000 },
  'SOLUSDT': { symbol: 'SOLUSDT', name: 'Solana', price: 145.67, change24h: 8.92, volume24h: 2100000000 },
  'AVAXUSDT': { symbol: 'AVAXUSDT', name: 'Avalanche', price: 34.21, change24h: -2.17, volume24h: 320000000 },
  'MATICUSDT': { symbol: 'MATICUSDT', name: 'Polygon', price: 1.23, change24h: 4.56, volume24h: 280000000 }
};

const Home = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [marketData] = useState(mockMarketData);

  const features = [
    {
      icon: '📊',
      title: 'Advanced Trading Tools',
      description: 'Professional-grade charts and analysis tools for informed trading decisions.',
      gradient: 'from-blue-500 to-cyan-500',
      benefits: ['Real-time charts', 'Technical indicators', 'Market analysis'],
      highlight: 'NEW'
    },
    {
      icon: '🔒',
      title: 'Bank-Level Security',
      description: 'Your assets are protected with industry-leading security measures.',
      gradient: 'from-green-500 to-emerald-500',
      benefits: ['2FA authentication', 'Cold storage', 'Insurance coverage'],
      highlight: 'TRUSTED'
    },
    {
      icon: '⚡',
      title: 'Lightning Fast Execution',
      description: 'Execute trades in milliseconds with our high-performance infrastructure.',
      gradient: 'from-purple-500 to-pink-500',
      benefits: ['0.1ms latency', '99.99% uptime', 'High-frequency trading'],
      highlight: 'FAST'
    },
    {
      icon: '📱',
      title: 'Mobile Trading',
      description: 'Trade on the go with our responsive mobile platform.',
      gradient: 'from-orange-500 to-red-500',
      benefits: ['iOS & Android apps', 'Push notifications', 'Offline mode'],
      highlight: 'MOBILE'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'Day Trader',
      content: 'The platform has revolutionized my trading experience. The speed and reliability are unmatched.',
      avatar: '👩‍💼'
    },
    {
      name: 'Michael Chen',
      role: 'Crypto Investor',
      content: 'Best trading platform I\'ve used. The analytics tools helped me increase my returns by 40%.',
      avatar: '👨‍💻'
    },
    {
      name: 'Emma Rodriguez',
      role: 'Portfolio Manager',
      content: 'Professional tools with an intuitive interface. Perfect for both beginners and experts.',
      avatar: '👩‍🎓'
    }
  ];

  const stats = [
    { label: 'Active Traders', value: '500K+', icon: '👥' },
    { label: 'Daily Volume', value: '$2.5B+', icon: '💰' },
    { label: 'Countries', value: '150+', icon: '🌍' },
    { label: 'Uptime', value: '99.9%', icon: '⚡' }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Convert marketData object to array and sort by market cap (price * volume as proxy)
  const marketDataArray = marketData ? 
    Object.values(marketData)
      .sort((a, b) => (b.price * b.volume24h) - (a.price * a.volume24h))
    : [];

  // Get symbol icon/avatar
  const getSymbolIcon = (symbol) => {
    const icons = {
      'BTCUSDT': '₿',
      'ETHUSDT': 'Ξ',
      'ADAUSDT': '₳',
      'DOTUSDT': '●',
      'LINKUSDT': '🔗',
      'SOLUSDT': '◎',
      'AVAXUSDT': '🔺',
      'MATICUSDT': '🔷'
    };
    return icons[symbol] || symbol.slice(0, 2);
  };

  // Scrolling ticker component
  const ScrollingTicker = () => {
    const tickerData = marketDataArray.length > 0 ? marketDataArray : [];
    
    if (tickerData.length === 0) {
      return (
        <div className="bg-slate-800/50 border-b border-slate-700/50 py-3">
          <div className="flex items-center justify-center text-slate-400">
            <span>Loading market data...</span>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b border-slate-700/50 py-3 overflow-hidden">
        <div className="relative">
          <div 
            className="flex items-center space-x-8 whitespace-nowrap animate-scroll"
          >
            {[...tickerData, ...tickerData, ...tickerData].map((coin, index) => (
              <div key={`${coin.symbol}-${index}`} className="flex items-center space-x-3 text-sm hover:bg-slate-700/30 px-3 py-1 rounded-lg transition-colors cursor-pointer">
                <span className="text-white font-medium text-lg">{getSymbolIcon(coin.symbol)}</span>
                <span className="text-white font-medium">{coin.symbol.replace('USDT', '/USDT')}</span>
                <span className="text-white font-bold">{formatPrice(coin.price)}</span>
                <span className={`flex items-center font-medium ${coin.change24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  <span className="mr-1">{coin.change24h >= 0 ? '↗' : '↘'}</span>
                  {formatPercentage(Math.abs(coin.change24h))}
                </span>
                <span className="text-slate-400 text-xs">
                  Vol: ${(coin.volume24h / 1000000000).toFixed(1)}B
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const handleStartTrading = () => {
    navigate('/signup');
  };

  const handleTryDemo = () => {
    // Navigate to the smart practice flow
    navigate('/signup'); // For now, redirect to signup
  };

  const handleLearnMore = () => {
    navigate('/about');
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* CSS Styles */}
      <style>
        {`
          @keyframes scroll {
            0% { transform: translateX(0%); }
            100% { transform: translateX(-33.333%); }
          }
          .animate-scroll {
            animation: scroll 40s linear infinite;
          }
        `}
      </style>

      {/* Header */}
      <Header />
      
      {/* Scrolling Market Ticker */}
      <ScrollingTicker />

      {/* Main Content */}
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-32">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                <h1 className="text-5xl lg:text-7xl font-bold text-white leading-tight">
                  GainwiseFX
                  <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                    {" "}AI Trading Platform
                  </span>
                </h1>
                <p className="text-xl text-slate-300 leading-relaxed">
                  Join millions of traders worldwide on the most trusted cryptocurrency exchange platform. 
                  Start your trading journey with advanced tools and unmatched security.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    size="lg" 
                    className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white transform hover:scale-105 transition-all duration-300 shadow-lg"
                    onClick={handleStartTrading}
                  >
                    Start Trading Now
                  </Button>
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="w-full sm:w-auto text-white border-2 border-white/20 hover:bg-white/10 hover:border-white/40 transition-all duration-300"
                    onClick={handleTryDemo}
                  >
                    Try Smart Practice
                  </Button>
                </div>
              </div>

              <div className="relative">
                {/* Hero Image */}
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl blur-xl" />
                  <div className="relative bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 shadow-2xl">
                    <div className="flex items-center justify-center h-64 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-xl overflow-hidden">
                      <div className="relative w-full h-full">
                        {/* Trading Dashboard Mockup */}
                        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl p-4">
                          {/* Header with price */}
                          <div className="flex justify-between items-center mb-4">
                            <div className="flex items-center space-x-2">
                              <div className="w-8 h-8 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full flex items-center justify-center text-white font-bold">₿</div>
                              <div>
                                <p className="text-white font-semibold">BTC/USDT</p>
                                <p className="text-green-400 text-sm">+2.34%</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-white text-xl font-bold">$67,890.45</p>
                              <p className="text-green-400 text-sm">+$1,567.23</p>
                            </div>
                          </div>
                          
                          {/* Mock Chart */}
                          <div className="h-32 bg-slate-800/50 rounded-lg mb-4 relative overflow-hidden">
                            {/* Animated chart lines */}
                            <svg className="w-full h-full" viewBox="0 0 200 80">
                              <defs>
                                <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                  <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.3"/>
                                  <stop offset="100%" stopColor="#3B82F6" stopOpacity="0"/>
                                </linearGradient>
                              </defs>
                              <path
                                d="M0,60 Q25,45 50,40 T100,35 T150,30 T200,25"
                                stroke="#3B82F6"
                                strokeWidth="2"
                                fill="none"
                                className="animate-pulse"
                              />
                              <path
                                d="M0,60 Q25,45 50,40 T100,35 T150,30 T200,25 L200,80 L0,80 Z"
                                fill="url(#chartGradient)"
                              />
                            </svg>
                          </div>
                          
                          {/* Trading buttons */}
                          <div className="grid grid-cols-2 gap-2">
                            <button className="bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-lg font-medium transition-colors">
                              BUY
                            </button>
                            <button className="bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg font-medium transition-colors">
                              SELL
                            </button>
                          </div>
                        </div>
                        
                        {/* Floating elements */}
                        <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full animate-bounce">
                          Live
                        </div>
                        <div className="absolute -bottom-2 -left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                          24/7
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-20 bg-slate-800/30 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div
                  key={stat.label}
                  className="text-center group hover:scale-105 transition-transform duration-300"
                >
                  <div className="text-4xl mb-2 group-hover:scale-110 transition-transform duration-300">
                    {stat.icon}
                  </div>
                  <div className="text-3xl font-bold text-white mb-1 group-hover:text-blue-300 transition-colors">
                    {stat.value}
                  </div>
                  <div className="text-slate-400 group-hover:text-slate-300 transition-colors">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Enhanced Features Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">Why Choose Our Platform?</h2>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                Experience the next generation of cryptocurrency trading with our cutting-edge platform
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => (
                <div
                  key={feature.title}
                  className="group hover:-translate-y-2 transition-all duration-300"
                >
                  <Card className="h-full bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm border border-slate-700/50 hover:border-blue-500/50 hover:shadow-2xl hover:shadow-blue-500/20 transition-all duration-300 relative overflow-hidden">
                    {/* Highlight badge */}
                    <div className={`absolute top-3 right-3 px-2 py-1 text-xs font-bold rounded-full ${
                      feature.highlight === 'NEW' ? 'bg-green-500 text-white' :
                      feature.highlight === 'TRUSTED' ? 'bg-blue-500 text-white' :
                      feature.highlight === 'FAST' ? 'bg-purple-500 text-white' :
                      'bg-orange-500 text-white'
                    }`}>
                      {feature.highlight}
                    </div>
                    
                    <div className="p-6 text-center">
                      <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r ${feature.gradient} mb-4 text-2xl group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                        {feature.icon}
                      </div>
                      <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-blue-300 transition-colors">
                        {feature.title}
                      </h3>
                      <p className="text-slate-300 leading-relaxed mb-4">{feature.description}</p>
                      
                      {/* Benefits list */}
                      <div className="space-y-2">
                        {feature.benefits.map((benefit, idx) => (
                          <div key={idx} className="flex items-center text-sm text-slate-400 group-hover:text-slate-300 transition-colors">
                            <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-2 group-hover:bg-blue-300"></div>
                            {benefit}
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Animated background effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 via-blue-500/5 to-purple-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-20 bg-slate-800/30 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">Trusted by Traders Worldwide</h2>
              <p className="text-xl text-slate-300">See what our community has to say</p>
            </div>

            <div className="relative">
              <div key={currentTestimonial} className="transition-all duration-500">
                <Card className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm border-slate-700/50 hover:border-blue-500/30 transition-all duration-300">
                  <div className="p-8 text-center">
                    <div className="text-6xl mb-4">{testimonials[currentTestimonial].avatar}</div>
                    <blockquote className="text-xl text-white mb-6 italic">
                      "{testimonials[currentTestimonial].content}"
                    </blockquote>
                    <div>
                      <p className="font-semibold text-white">{testimonials[currentTestimonial].name}</p>
                      <p className="text-slate-400">{testimonials[currentTestimonial].role}</p>
                    </div>
                  </div>
                </Card>
              </div>
              
              <div className="flex justify-center mt-6 space-x-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonial(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentTestimonial 
                        ? 'bg-blue-500 scale-125' 
                        : 'bg-slate-600 hover:bg-slate-500'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-2xl p-12 backdrop-blur-sm border border-slate-700/50">
              <h2 className="text-4xl font-bold text-white mb-6">Ready to Start Trading?</h2>
              <p className="text-xl text-slate-300 mb-8">
                Join thousands of successful traders and start your journey today
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 text-white shadow-lg"
                  onClick={handleStartTrading}
                >
                  Create Free Account
                </Button>
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="w-full sm:w-auto text-white border-2 border-white/20 hover:bg-white/10 hover:border-white/40 transition-all duration-300"
                  onClick={handleLearnMore}
                >
                  Learn More
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Home;
